import styled from 'styled-components'
import { vimovaTheme } from '../../theme/vimovaTheme'

export const AddressLine = styled.p`
  font-family: ${vimovaTheme.fonts.body};
  font-size: 10px;
  color: ${vimovaTheme.colors.textLight};
  margin: 6px 0 0 0;

  @media print {
    font-size: 10px !important;
    color: ${vimovaTheme.colors.textLight} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const TableSection = styled.div`
  margin-top: 16px;

  @media print {
    margin-top: 16px !important;
  }
`

export const SubSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  @media print {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    margin-bottom: 8px !important;
  }
`

export const SubSectionIcon = styled.div`
  flex-shrink: 0;
  color: ${vimovaTheme.colors.warmGray};

  svg {
    display: block;
  }

  @media print {
    color: ${vimovaTheme.colors.warmGray} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const TableSectionTitle = styled.div`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: 12px;
  color: ${vimovaTheme.colors.text};

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: 12px !important;
    color: ${vimovaTheme.colors.text} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const TableCard = styled.div`
  overflow: hidden;
`

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${vimovaTheme.fonts.body};
  font-size: ${vimovaTheme.fontSizes.micro};

  @media print {
    width: 100% !important;
    border-collapse: collapse !important;
    font-size: ${vimovaTheme.fontSizes.micro} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const TableHead = styled.thead`
  th {
    font-family: ${vimovaTheme.fonts.body};
    font-size: 8.5px;
    font-weight: ${vimovaTheme.fontWeights.bold};
    color: ${vimovaTheme.colors.textMuted};
    padding: 8px 6px;
    text-align: left;
    border-bottom: 1px solid ${vimovaTheme.colors.border};
  }

  @media print {
    th {
      font-weight: ${vimovaTheme.fontWeights.bold} !important;
      color: ${vimovaTheme.colors.textMuted} !important;
      border-bottom: 1px solid ${vimovaTheme.colors.border} !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

export const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid ${vimovaTheme.colors.border};

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    font-family: ${vimovaTheme.fonts.body};
    font-size: ${vimovaTheme.fontSizes.micro};
    color: ${vimovaTheme.colors.text};
    padding: 8px 6px;
  }

  @media print {
    tr {
      border-bottom: 1px solid ${vimovaTheme.colors.border} !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    td {
      font-size: ${vimovaTheme.fontSizes.micro} !important;
      color: ${vimovaTheme.colors.text} !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

export const PriceCell = styled.td`
  && {
    font-weight: ${vimovaTheme.fontWeights.bold};
    color: ${vimovaTheme.colors.greenDark};
  }

  @media print {
    && {
      font-weight: ${vimovaTheme.fontWeights.bold} !important;
      color: ${vimovaTheme.colors.greenDark} !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

export const StatusBadge = styled.span<{ $active?: boolean }>`
  display: inline-block;
  font-family: ${vimovaTheme.fonts.body};
  font-size: 7.5px;
  font-weight: ${vimovaTheme.fontWeights.bold};
  padding: 3px 10px;
  border-radius: 8px;
  background: ${({ $active }) =>
    $active ? 'rgba(165, 204, 116, 0.2)' : 'rgba(146, 146, 146, 0.15)'};
  color: ${({ $active }) =>
    $active ? vimovaTheme.colors.greenDark : vimovaTheme.colors.textMuted};

  @media print {
    background: ${({ $active }) =>
      $active
        ? 'rgba(165, 204, 116, 0.2)'
        : 'rgba(146, 146, 146, 0.15)'} !important;
    color: ${({ $active }) =>
      $active
        ? vimovaTheme.colors.greenDark
        : vimovaTheme.colors.textMuted} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PermitStatusBadge = styled.span<{
  $type?: 'approved' | 'pending'
}>`
  display: inline-block;
  font-family: ${vimovaTheme.fonts.body};
  font-size: 7.5px;
  font-weight: ${vimovaTheme.fontWeights.bold};
  padding: 3px 10px;
  border-radius: 8px;
  background: ${({ $type }) =>
    $type === 'approved'
      ? 'rgba(165, 204, 116, 0.2)'
      : 'rgba(239, 113, 63, 0.15)'};
  color: ${({ $type }) =>
    $type === 'approved'
      ? vimovaTheme.colors.greenDark
      : vimovaTheme.colors.orange};

  @media print {
    background: ${({ $type }) =>
      $type === 'approved'
        ? 'rgba(165, 204, 116, 0.2)'
        : 'rgba(239, 113, 63, 0.15)'} !important;
    color: ${({ $type }) =>
      $type === 'approved'
        ? vimovaTheme.colors.greenDark
        : vimovaTheme.colors.orange} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const NoDataText = styled.div`
  font-family: ${vimovaTheme.fonts.body};
  font-size: ${vimovaTheme.fontSizes.small};
  color: ${vimovaTheme.colors.textMuted};
  text-align: center;
  padding: 24px;

  @media print {
    font-size: ${vimovaTheme.fontSizes.small} !important;
    color: ${vimovaTheme.colors.textMuted} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`
